import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SettingsProvider } from "@/contexts/SettingsContext";

import PublicLayout from "@/components/layout/PublicLayout";
import HomeRoute from "@/pages/HomeRoute";

// Public routes — lazy loaded (Home is eager for fastest LCP)
const Products = lazy(() => import("@/pages/Products"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const ProductOrCategoryResolver = lazy(() => import("@/pages/ProductOrCategoryResolver"));
const ProductSubcategoryPage = lazy(() => import("@/pages/ProductSubcategoryPage"));
const Industries = lazy(() => import("@/pages/Industries"));
const IndustryDetail = lazy(() => import("@/pages/IndustryDetail"));
const Resources = lazy(() => import("@/pages/Resources"));
const Catalogues = lazy(() => import("@/pages/resources/Catalogues"));
const Datasheets = lazy(() => import("@/pages/resources/Datasheets"));
const OperationManuals = lazy(() => import("@/pages/resources/OperationManuals"));
const ResourceVideos = lazy(() => import("@/pages/resources/Videos"));
const CaseStudies = lazy(() => import("@/pages/resources/CaseStudies"));
const CaseStudyDetail = lazy(() => import("@/pages/resources/CaseStudyDetail"));
const ResourceFaqs = lazy(() => import("@/pages/resources/Faqs"));
const EngineeringArticles = lazy(() => import("@/pages/resources/EngineeringArticles"));
const ArticleDetail = lazy(() => import("@/pages/resources/ArticleDetail"));
const ResourceCertificates = lazy(() => import("@/pages/resources/Certificates"));
const DynamicPage = lazy(() => import("@/pages/DynamicPage"));
const CompanyAbout = lazy(() => import("@/pages/company/About"));
const ManufacturingFacility = lazy(() => import("@/pages/company/ManufacturingFacility"));
const QualityCertifications = lazy(() => import("@/pages/company/QualityCertifications"));
const GlobalPresence = lazy(() => import("@/pages/company/GlobalPresence"));
const Services = lazy(() => import("@/pages/Services"));
const ServiceDetail = lazy(() => import("@/pages/ServiceDetail"));
const Contact = lazy(() => import("@/pages/Contact"));
const SearchPage = lazy(() => import("@/pages/Search"));
const NotFound = lazy(() => import("@/pages/NotFound"));

// Admin — lazy loaded (never on public bundle)
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const Dashboard = lazy(() => import("@/pages/admin/Dashboard"));
const ProductsAdmin = lazy(() => import("@/pages/admin/ProductsAdmin"));
const IndustriesAdmin = lazy(() => import("@/pages/admin/IndustriesAdmin"));
const LeadsAdmin = lazy(() => import("@/pages/admin/LeadsAdmin"));
const FaqsAdmin = lazy(() => import("@/pages/admin/FaqsAdmin"));
const TestimonialsAdmin = lazy(() => import("@/pages/admin/TestimonialsAdmin"));
const ResourcesAdmin = lazy(() => import("@/pages/admin/ResourcesAdmin"));
const BlogAdmin = lazy(() => import("@/pages/admin/BlogAdmin"));
const PagesAdmin = lazy(() => import("@/pages/admin/PagesAdmin"));
const PageBuilder = lazy(() => import("@/pages/admin/PageBuilder"));
const HomePageAdmin = lazy(() => import("@/pages/admin/HomePageAdmin"));
const MediaLibraryAdmin = lazy(() => import("@/pages/admin/MediaLibraryAdmin"));
const ChangePassword = lazy(() => import("@/pages/admin/ChangePassword"));
const SettingsAdmin = lazy(() => import("@/pages/admin/SettingsAdmin"));
const MenusAdmin = lazy(() => import("@/pages/admin/MenusAdmin"));
const UsersAdmin = lazy(() => import("@/pages/admin/UsersAdmin"));
const AnalyticsAdmin = lazy(() => import("@/pages/admin/AnalyticsAdmin"));
const MegaMenuAdmin = lazy(() => import("@/pages/admin/MegaMenuAdmin"));

import "@/App.css";

function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" role="status" aria-live="polite" data-testid="route-loading">
      <div className="flex items-center gap-3 text-[#6C757D]">
        <span className="inline-block w-5 h-5 rounded-full border-2 border-[#2563EB] border-t-transparent animate-spin" aria-hidden="true" />
        <span className="text-xs font-heading font-bold uppercase tracking-widest">Loading…</span>
      </div>
    </div>
  );
}

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#6C757D]">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.must_change_password) return <Navigate to="/admin/change-password" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/admin/dashboard" replace />;
  return children;
}

function ChangePasswordGuard({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <BrowserRouter>
          <a
            href="#main-content"
            className="sr-only focus:not-sr-1only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:bg-[#2563EB] focus:text-white focus:px-4 focus:py-2 focus:rounded focus:font-heading focus:text-xs focus:font-bold focus:uppercase focus:tracking-widest"
            data-testid="skip-to-content"
          >
            Skip to main content
          </a>
          <Toaster position="top-right" richColors closeButton />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<HomeRoute />} />
                <Route path="/products" element={<Products />} />
                {/* :slug is deliberately ambiguous — it resolves to either the
                    category page or the flat (sitemap-critical) product page;
                    see ProductOrCategoryResolver for why these can't be two
                    separate static routes. */}
                <Route path="/products/:slug" element={<ProductOrCategoryResolver />} />
                {/* Tube Cleaners was promoted from a Tube Tools subcategory to its own top-level category
                    (/products/tube-cleaners) — this keeps old bookmarked/indexed links working. */}
                <Route path="/products/tube-tool/tube-cleaners" element={<Navigate to="/products/tube-cleaners" replace />} />
                <Route path="/products/:categorySlug/:subcategorySlug" element={<ProductSubcategoryPage />} />
                <Route path="/products/:categorySlug/:subcategorySlug/:slug" element={<ProductDetail />} />
                <Route path="/industries" element={<Industries />} />
                <Route path="/industries/:slug" element={<IndustryDetail />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/resources/catalogues" element={<Catalogues />} />
                <Route path="/resources/datasheets" element={<Datasheets />} />
                <Route path="/resources/operation-manuals" element={<OperationManuals />} />
                <Route path="/resources/videos" element={<ResourceVideos />} />
                <Route path="/resources/case-studies" element={<CaseStudies />} />
                <Route path="/resources/case-studies/:slug" element={<CaseStudyDetail />} />
                <Route path="/resources/faqs" element={<ResourceFaqs />} />
                <Route path="/resources/engineering-articles" element={<EngineeringArticles />} />
                <Route path="/resources/engineering-articles/:slug" element={<ArticleDetail />} />
                <Route path="/resources/certificates" element={<ResourceCertificates />} />
                <Route path="/pages/:slug" element={<DynamicPage />} />
                <Route path="/company/about" element={<CompanyAbout />} />
                <Route path="/company/manufacturing-facility" element={<ManufacturingFacility />} />
                <Route path="/company/quality-certifications" element={<QualityCertifications />} />
                <Route path="/company/global-presence" element={<GlobalPresence />} />
                <Route path="/about" element={<Navigate to="/company/about" replace />} />
                <Route path="/services" element={<Services />} />
                <Route path="/services/:slug" element={<ServiceDetail />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="*" element={<NotFound />} />
              </Route>

              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/change-password" element={<ChangePasswordGuard><ChangePassword /></ChangePasswordGuard>} />
              <Route path="/admin/pages/:pageId" element={<ProtectedRoute><PageBuilder /></ProtectedRoute>} />

              <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="pages" element={<PagesAdmin />} />
                <Route path="home-page" element={<HomePageAdmin />} />
                <Route path="products" element={<ProductsAdmin />} />
                <Route path="industries" element={<IndustriesAdmin />} />
                <Route path="resources" element={<ResourcesAdmin />} />
                <Route path="articles" element={<BlogAdmin />} />
                <Route path="faqs" element={<FaqsAdmin />} />
                <Route path="testimonials" element={<TestimonialsAdmin />} />
                <Route path="media" element={<MediaLibraryAdmin />} />
                <Route path="leads" element={<LeadsAdmin />} />
                <Route path="analytics" element={<ProtectedRoute roles={["super_admin", "admin", "marketing"]}><AnalyticsAdmin /></ProtectedRoute>} />
                <Route path="menus" element={<ProtectedRoute roles={["super_admin", "admin"]}><MenusAdmin /></ProtectedRoute>} />
                <Route path="mega-menu" element={<ProtectedRoute roles={["super_admin", "admin", "editor"]}><MegaMenuAdmin /></ProtectedRoute>} />
                <Route path="settings" element={<ProtectedRoute roles={["super_admin", "admin"]}><SettingsAdmin /></ProtectedRoute>} />
                <Route path="users" element={<ProtectedRoute roles={["super_admin"]}><UsersAdmin /></ProtectedRoute>} />
                <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
      </SettingsProvider>
    </AuthProvider>
  );
}
